import React, { useEffect, useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import classnames from 'classnames';
import { Checkbox, Group, Popover, ScrollArea, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { ISelectedMatch, ISourceMatch, IDataCut, IFormattedMatch } from '../PropagateMatchesTypes';

type ToolTipProps = {
  label: string
};

type RowProps = {
  updateToggledMatches: (id: string) => void,
  surveyJobMatch: ISourceMatch,
  toggledTargetMatches: string[],
  setSurveyJobMatches: React.Dispatch<React.SetStateAction<ISourceMatch[]>>;
  propagateMatch: IFormattedMatch,
};

const RenderTooltip: React.FC<ToolTipProps> = ({ label }) => (
  <Tooltip label={label}>
    <div className="icon-border icon-circle-thin no-data-available">
      <i className="icon-attention-alt" />
    </div>
  </Tooltip>
);

const MatchRow: React.FC<RowProps> = ({
  updateToggledMatches,
  surveyJobMatch,
  toggledTargetMatches,
  setSurveyJobMatches,
  propagateMatch,
}) => {
  const selectedCut = useMemo(() => propagateMatch?.dataCuts?.find((cut: IDataCut) => surveyJobMatch?.selectedPropagateRows?.some((selectedCut: ISelectedMatch) => selectedCut.cutId === cut.cutId && selectedCut.targetJobId === propagateMatch.targetJobId && selectedCut.surveyCode === propagateMatch.surveyCode )), [surveyJobMatch]);
  const [opened, { close, open }] = useDisclosure(false);
  const [popoverHovered, setPopoverHovered] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);
  const isToggleDisabled = !propagateMatch.hasDefaultAndPriorityCut && !selectedCut;
  const uniqueIdentifier = selectedCut?.uniqueIdentifier;

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        if (!popoverHovered && !titleHovered) close();
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [opened, popoverHovered, titleHovered]);

  const getCheckBoxTooltip = useMemo(() => {
    if (selectedCut?.matchExists) {
      return {
        label: 'This match already exists and will not be added again',
        class: 'existing-match',
        icon: 'icon-check',
      };
    }
    if (!propagateMatch.hasDefaultAndPriorityCut && propagateMatch.surveyJobCode && !selectedCut) {
      return {
        label: 'Select a data cut to apply',
        class: 'icon-border icon-circle-thin no-datacut',
        icon: 'icon-attention-alt',
      };
    }
    return null;
  }, [propagateMatch, selectedCut]);

  const updateSelectedRows = ({
    match, targetMatch, datacut, isChecked
  }: {
    match: ISourceMatch,
    targetMatch: IFormattedMatch,
    datacut?: IDataCut,
    isChecked?: boolean
  }) => {
    const { selectedPropagateRows } = match;
    const { targetJobId, surveyCode, hasData } = targetMatch;

    if (!datacut) {
      return selectedPropagateRows.map((row: ISelectedMatch) =>
        row.targetJobId === targetJobId && row.surveyCode === surveyCode ? { ...row, isChecked: !!isChecked } : row
      );
    }

    const { cutId, matchExists } = datacut;
    const rowExists = selectedPropagateRows.some((row: ISelectedMatch) => row.targetJobId === targetJobId && row.surveyCode === surveyCode);

    if (matchExists && rowExists) {
      return selectedPropagateRows.map((row: ISelectedMatch) =>
        row.targetJobId === targetJobId && row.surveyCode === surveyCode ? { ...row, cutId, isChecked: false, matchExists } : row
      );
    }

    if (matchExists && !rowExists) {
      return [
        ...selectedPropagateRows,
        { targetJobId, cutId, surveyCode, isChecked: false, hasData, matchExists }
      ];
    }

    if (rowExists) {
      return selectedPropagateRows.map((row: ISelectedMatch) =>
        row.targetJobId === targetJobId && row.surveyCode === surveyCode
          ? { ...row, cutId, isChecked: true, hasData, matchExists }
          : row
      );
    }

    return [
      ...selectedPropagateRows,
      { targetJobId, cutId, surveyCode, isChecked: true, hasData, matchExists }
    ];
  };

  const handleDatacutChange = (cut: string) => {
    setSurveyJobMatches((prevSurveyJobMatches: ISourceMatch[]) =>
      prevSurveyJobMatches.map(match => {
        if (match.sourceMatchId !== surveyJobMatch.sourceMatchId) return match;

        let updatedCut: IDataCut | undefined;
        let updatedTargetMatch: IFormattedMatch | undefined;
        const updatedPropagateMatches = match.propagateMatches.map(targetMatch => {
          if (
            targetMatch.targetJobId === propagateMatch.targetJobId &&
            targetMatch.surveyCode === propagateMatch.surveyCode &&
            targetMatch.surveyJobCode === propagateMatch.surveyJobCode
          ) {
            updatedTargetMatch = targetMatch;
            updatedCut = targetMatch.dataCuts.find(datacut => datacut.datacutName === cut);
          }
          return targetMatch;
        });
        const selectedPropagateRows =
          updatedTargetMatch && updatedCut
            ? updateSelectedRows({match, targetMatch: updatedTargetMatch, datacut: updatedCut, isChecked: true})
            : match.selectedPropagateRows;

        return {
          ...match,
          propagateMatches: updatedPropagateMatches,
          selectedPropagateRows
        };
      })
    );
  };

  const handleToggle = () => {
    if (!isToggleDisabled && uniqueIdentifier) {
      updateToggledMatches(uniqueIdentifier);
    }
  };

  const renderDataCell = (data: string, tooltipLabel: string) => (
    data ? data : <RenderTooltip label={tooltipLabel} />
  );

  const updateSelectedPropagateMatches = (
    event: React.ChangeEvent<HTMLInputElement>,
    surveyJobMatch: ISourceMatch,
    propagateMatch: IFormattedMatch
  ) => {
    const isChecked = event.target.checked;
    setSurveyJobMatches((prevSurveyJobMatches: ISourceMatch[]) =>
      prevSurveyJobMatches.map((match: ISourceMatch) =>
        match.sourceMatchId === surveyJobMatch.sourceMatchId
          ? {
              ...match,
              selectedPropagateRows: updateSelectedRows({
                match,
                targetMatch: propagateMatch,
                isChecked
              })
            }
          : match
      )
    );
  };

  return (
    <>
      <Table.Tr>
        <Table.Td>
          {propagateMatch.surveyJobCode ? (
            <Group>
              {isToggleDisabled ? (
                <Tooltip label="No data cut selected">
                  <i className={classnames('icon-up-open', 'disabled-toggle')} />
                </Tooltip>
              ) : (
                <i
                  className={classnames({
                    'icon-up-open': uniqueIdentifier && !toggledTargetMatches.includes(uniqueIdentifier),
                    'icon-down-open': uniqueIdentifier && toggledTargetMatches.includes(uniqueIdentifier)
                  })}
                  onClick={handleToggle}
                />
              )}
              {propagateMatch.surveyJobCode}
            </Group>
          ) : (
            renderDataCell(propagateMatch.surveyJobCode, 'No survey data available')
          )}
        </Table.Td>
        <Table.Td>{renderDataCell(propagateMatch.surveyJobTitle, 'No survey data available')}</Table.Td>
        <Table.Td>
          <Group gap="xs">
            {propagateMatch.pricingStatus === "slotted" && (
              <Tooltip label="Target job is slotted">
                <Text size="l">*</Text>
              </Tooltip>
            )}
           {propagateMatch.targetJobCodeKey}
          </Group>
        </Table.Td>
        <Table.Td>{renderDataCell(propagateMatch.surveyCode, 'No survey data available')}</Table.Td>
        <Table.Td>{renderDataCell(propagateMatch.surveyTitle, 'No survey data available')}</Table.Td>
        <Table.Td>{propagateMatch.targetCurrency}</Table.Td>
        <Table.Td>
          <Select
            className={classnames({ 'datacut-select__error': !selectedCut && propagateMatch.surveyJobCode })}
            data={propagateMatch?.dataCuts?.map((cut: IDataCut) => cut.datacutName) || []}
            value={selectedCut?.datacutName || 'None Selected'}
            onChange={cut => cut && handleDatacutChange(cut)}
            placeholder="None Selected"
            disabled={!propagateMatch?.dataCuts?.length || (propagateMatch?.dataCuts?.length === 1 && selectedCut?.matchExists)}
            searchable
          />
        </Table.Td>
        <Table.Td>
          {getCheckBoxTooltip ? (
            <Group justify="center">
              <Tooltip label={getCheckBoxTooltip.label}>
                <div className={getCheckBoxTooltip.class}>
                  <i className={getCheckBoxTooltip.icon} />
                </div>
              </Tooltip>
            </Group>
          ) : (
            <Group justify="center">
              <Checkbox
                disabled={!propagateMatch.hasData}
                checked={!!surveyJobMatch.selectedPropagateRows.find((match: ISelectedMatch) => {
                  return (
                    match.targetJobId === propagateMatch.targetJobId &&
                    match.surveyCode === propagateMatch.surveyCode &&
                    match.isChecked
                  );
                })}
                onChange={event => updateSelectedPropagateMatches(event, surveyJobMatch, propagateMatch)}
                size="md"
              />
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
      <Table.Tr style={{visibility: selectedCut && toggledTargetMatches.includes(selectedCut.uniqueIdentifier) ? 'visible' : 'collapse'}}>
        <Table.Td colSpan={8} p="20 60">
          <Group justify="space-between" wrap="nowrap" gap="xl">
            <Stack m="0 20">
              <Title size="h6">Job description</Title>
              <Popover opened={opened} position="bottom" shadow="xl" withArrow>
                <Popover.Target>
                  <Text
                    lineClamp={2}
                    onMouseEnter={() => {
                      setTitleHovered(true);
                      open();
                    }}
                    onMouseLeave={() => {
                      setTitleHovered(false);
                    }}
                  >
                    {selectedCut?.jobDescription || '-'}
                  </Text>
                </Popover.Target>
                <Popover.Dropdown
                  maw={700}
                  onMouseEnter={() => setPopoverHovered(true)}
                  onMouseLeave={() => {
                    setPopoverHovered(false);
                    close();
                  }}
                >
                  <ScrollArea h={selectedCut?.jobDescription && selectedCut?.jobDescription?.length > 700 ? 300 : ''}>
                     <Text mr='20px' size="sm">{selectedCut?.jobDescription}</Text>
                  </ScrollArea>
                </Popover.Dropdown>
              </Popover>
            </Stack>
            <Group wrap="nowrap">
              <Stack m="0 20">
                <Title size="h6">Companies</Title>
                <Text>{selectedCut?.totalCompanies || '-'}</Text>
              </Stack>
              <Stack m="0 20">
                <Title size="h6">Incumbents</Title>
                <Text>{selectedCut?.totalIncumbents || '-'}</Text>
              </Stack>
            </Group>
          </Group>
        </Table.Td>
      </Table.Tr>
    </>
  );
};

export default MatchRow;
